/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  if (customElements.get('bundle-picker')) return;

  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) return '0';
    var parts = (cents / 100).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(cents, format) {
    var match = String(format || '').match(MONEY_PLACEHOLDER);
    var value;

    switch (match && match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return String(format || '${{amount}}').replace(MONEY_PLACEHOLDER, value);
  }

  function writePrice(element, formatted) {
    if (!element) return;
    var hidden = element.querySelector('.visually-hidden');
    var hiddenText = hidden ? hidden.textContent : '';
    element.textContent = '';
    if (hiddenText) {
      var span = document.createElement('span');
      span.className = 'visually-hidden';
      span.textContent = hiddenText;
      element.appendChild(span);
    }
    element.appendChild(document.createTextNode(formatted));
  }

  var BundlePicker = class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.adding = false;
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.sync();
      this.classList.add('is-ready');
    }

    moneyFormat() {
      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      if (settings.currencyCodeEnabled && settings.moneyWithCurrencyFormat) {
        return settings.moneyWithCurrencyFormat;
      }
      return settings.moneyFormat || '${{amount}}';
    }

    currentBreak() {
      return this.querySelector('.bundle-picker__break-button[aria-checked="true"]');
    }

    cap() {
      var current = this.currentBreak();
      return current ? parseInt(current.getAttribute('data-quantity'), 10) || 0 : 0;
    }

    // DRESSi "bigger bundles": when the section allows it and the LARGEST size
    // is selected, that size becomes a minimum instead of an exact count, up
    // to data-max-total. Off (or any smaller size) = the original exact rule.
    extendable() {
      if (this.getAttribute('data-allow-extra') !== 'true') return false;
      var sizes = Array.prototype.map.call(this.querySelectorAll('.bundle-picker__break-button'), function (b) {
        return parseInt(b.getAttribute('data-quantity'), 10) || 0;
      });
      var cap = this.cap();
      return cap > 0 && cap === Math.max.apply(null, sizes);
    }

    maxTotal() {
      var cap = this.cap();
      if (!this.extendable()) return cap;
      return Math.max(cap, parseInt(this.getAttribute('data-max-total'), 10) || cap);
    }

    isComplete(selected, cap) {
      if (!(cap > 0)) return false;
      return this.extendable() ? selected >= cap && selected <= this.maxTotal() : selected === cap;
    }

    rows() {
      return Array.prototype.slice.call(this.querySelectorAll('.bundle-picker__product'));
    }

    selectedCount() {
      return this.rows().reduce(function (sum, row) {
        return sum + (parseInt(row.getAttribute('data-qty'), 10) || 0);
      }, 0);
    }

    isPercentage() {
      return String(this.getAttribute('data-reduction-mode') || '').indexOf('percent') !== -1;
    }

    discounted(subtotal, selected, cap) {
      var current = this.currentBreak();
      if (!current || subtotal <= 0) return 0;

      // Both modes wait for a complete bundle before showing a saving. The
      // discount is applied by Nerd Functions at the cart, and only to a full
      // bundle, so an early discount here would promise a price the cart
      // won't honour. Amount mode already did this; percentage did not.
      if (!this.isComplete(selected, cap)) return subtotal;

      if (this.isPercentage()) {
        var percentage = parseInt(current.getAttribute('data-reduction-percentage'), 10) || 0;
        return Math.max(0, Math.round(subtotal * (100 - percentage) / 100));
      }

      var amount = parseInt(current.getAttribute('data-reduction-amount'), 10) || 0;
      return Math.max(0, subtotal - amount);
    }

    resetQuantities() {
      this.rows().forEach(function (row) {
        row.setAttribute('data-qty', '0');
      });
    }

    selectBreak(button) {
      if (!button || button.getAttribute('aria-checked') === 'true') return;

      var previous = this.currentBreak();
      var previousQty = previous ? parseInt(previous.getAttribute('data-quantity'), 10) || 0 : 0;
      var nextQty = parseInt(button.getAttribute('data-quantity'), 10) || 0;

      if (nextQty < previousQty) this.resetQuantities();

      Array.prototype.forEach.call(this.querySelectorAll('.bundle-picker__break-button'), function (candidate) {
        var selected = candidate === button;
        candidate.setAttribute('aria-checked', selected ? 'true' : 'false');
        candidate.tabIndex = selected ? 0 : -1;
      });

      this.sync();
    }

    changeQuantity(row, delta) {
      if (!row || row.getAttribute('data-available') !== 'true') return;

      var qty = parseInt(row.getAttribute('data-qty'), 10) || 0;
      var next = qty + delta;
      if (next < 0) return;
      if (delta > 0 && this.selectedCount() >= this.maxTotal()) return;

      row.setAttribute('data-qty', String(next));
      this.sync();
    }

    sync() {
      var cap = this.cap();
      var max = this.maxTotal();
      var selected = this.selectedCount();
      var subtotal = 0;

      this.rows().forEach(function (row) {
        var qty = parseInt(row.getAttribute('data-qty'), 10) || 0;
        var price = parseInt(row.getAttribute('data-price'), 10) || 0;
        var available = row.getAttribute('data-available') === 'true';
        subtotal += qty * price;

        var label = row.querySelector('[data-qty-label]');
        if (label) label.textContent = String(qty);

        var decrease = row.querySelector('[data-action="decrease"]');
        var increase = row.querySelector('[data-action="increase"]');
        if (decrease) decrease.disabled = !available || qty <= 0;
        if (increase) increase.disabled = !available || selected >= max;
      });

      var finalPrice = this.discounted(subtotal, selected, cap);
      var formatted = formatMoney(finalPrice, this.moneyFormat());
      var priceNode = this.querySelector('.bundle-picker__submit-price');
      if (priceNode) priceNode.textContent = formatted;

      var complete = this.isComplete(selected, cap);
      var labelNode = this.querySelector('.bundle-picker__submit-label');
      if (labelNode) {
        labelNode.textContent = complete
          ? (this.getAttribute('data-label') || 'Add to cart')
          : (this.getAttribute('data-incomplete-label') || 'Choose all products');
      }
      var submit = this.querySelector('[data-bundle-submit]');
      if (submit) {
        submit.disabled = this.adding || !complete;
        submit.setAttribute('aria-disabled', submit.disabled ? 'true' : 'false');
      }

      var status = this.querySelector('[data-bundle-status]');
      var template = this.getAttribute('data-selected-template') || '__S__ of __T__';
      if (status) {
        if (selected > cap) template = this.getAttribute('data-extra-template') || '__S__ selected';
        status.textContent = template.replace('__S__', String(selected)).replace('__T__', String(cap));
      }

      this.syncPagePrice(finalPrice, subtotal, formatted, selected);
      this.setAttribute('data-price', String(finalPrice));
    }

    syncPagePrice(finalPrice, subtotal, formatted, selected) {
      var info = this.closest('.product__info');
      if (!info) return;

      var priceList = info.querySelector('.product-meta__price-list-container .price-list');
      if (!priceList) return;

      if (this.originalPriceListHtml == null) this.originalPriceListHtml = priceList.innerHTML;

      if (selected === 0) {
        priceList.innerHTML = this.originalPriceListHtml;
        return;
      }

      var price = priceList.querySelector('.price:not(.price--compare)');
      writePrice(price, formatted);

      var compare = priceList.querySelector('.price--compare');
      if (subtotal > finalPrice) {
        if (!compare) {
          compare = document.createElement('span');
          compare.className = 'price price--compare';
          compare.setAttribute('data-bundle-compare', '');
          priceList.appendChild(compare);
        }
        compare.hidden = false;
        writePrice(compare, formatMoney(subtotal, this.moneyFormat()));
      } else if (compare) {
        compare.hidden = true;
      }
    }

    showError(message) {
      var error = this.querySelector('[data-bundle-error]');
      if (!error) return;
      error.hidden = !message;
      error.textContent = message || '';
    }

    async add() {
      // Properties are mandatory for bundle picker to work. Do not remove this.
      var cap = this.cap();
      if (this.adding || cap < 1 || !this.isComplete(this.selectedCount(), cap)) return;

      var current = this.currentBreak();
      var stamp = String(Date.now());
      var parentId = this.getAttribute('data-parent-id');
      var reductionName = current ? current.getAttribute('data-name') : '';
      var items = [];
      this.rows().forEach(function (row) {
        var qty = parseInt(row.getAttribute('data-qty'), 10) || 0;
        if (qty < 1) return;
        items.push({
          id: parseInt(row.getAttribute('data-variant-id'), 10),
          quantity: qty,
          properties: {
            _bundleConfig: JSON.stringify({
              _bundleUniqueId: stamp,
              _bundleItemSize: qty,
              _bundleParentId: parentId,
              _reductionIdentifier: reductionName
            })
          }
        });
      });

      if (!items.length) return;

      var routes = (window.themeVariables && window.themeVariables.routes) || {};
      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      var submit = this.querySelector('[data-bundle-submit]');

      this.adding = true;
      this.showError('');
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
      }

      try {
        var response = await fetch(routes.cartAddUrl + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: items,
            sections: 'mini-cart'
          })
        });
        var responseJson = await response.json();

        if (!response.ok) {
          var description = responseJson.description || responseJson.message || this.getAttribute('data-error') || '';
          this.showError(description);
          this.dispatchEvent(new CustomEvent('cart-notification:show', {
            bubbles: true,
            cancelable: true,
            detail: { status: 'error', error: description }
          }));
          return;
        }

        if (settings.cartType === 'page' || settings.pageType === 'cart') {
          window.location.href = routes.cartUrl;
          return;
        }

        this.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: {
            variant: responseJson.items ? responseJson.items[0] : responseJson
          }
        }));

        var cartResponse = await fetch(routes.cartUrl + '.js');
        var cartContent = await cartResponse.json();
        cartContent.sections = responseJson.sections;

        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartContent }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: settings.cartType === 'drawer' && this.closest('.drawer') === null
          }
        }));
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'success', error: '' }
        }));

        this.resetQuantities();
      } catch (error) {
        this.showError(this.getAttribute('data-error') || '');
      } finally {
        this.adding = false;
        if (submit) submit.removeAttribute('aria-busy');
        this.sync();
      }
    }

    onClick(event) {
      var breakButton = event.target.closest('.bundle-picker__break-button');
      if (breakButton && this.contains(breakButton)) {
        this.selectBreak(breakButton);
        return;
      }

      var stepper = event.target.closest('[data-action]');
      if (stepper && this.contains(stepper)) {
        var row = stepper.closest('.bundle-picker__product');
        this.changeQuantity(row, stepper.getAttribute('data-action') === 'increase' ? 1 : -1);
        return;
      }

      if (event.target.closest('[data-bundle-submit]')) this.add();
    }

    onKeydown(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      var buttons = Array.prototype.slice.call(this.querySelectorAll('.bundle-picker__break-button'));
      var index = buttons.indexOf(document.activeElement);
      if (index < 0) return;

      event.preventDefault();
      var direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
      var next = buttons[(index + direction + buttons.length) % buttons.length];
      next.focus();
      this.selectBreak(next);
    }
  };

  customElements.define('bundle-picker', BundlePicker);
})();
/* --------------------------------------------------------------------------
   DRESSi: sticky side panels (see "Desktop: sticky side panels" in custom.css)

   Works out where each panel should stick. If it fits below the header it
   sticks 24px under it; if it's taller than the window, the offset goes
   negative so the panel's bottom stays reachable. Recalculated whenever the
   panel changes height (an accordion opens) or the window resizes.
   -------------------------------------------------------------------------- */
(() => {
  const SELECTOR = '.product--dressi-stacked > .product__info, .dressi-faq__media';
  const GAP = 24;
  const desktop = window.matchMedia('(min-width: 1000px)');

  const update = (el) => {
    if (!desktop.matches) {
      el.style.removeProperty('--dressi-sticky-top');
      return;
    }
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const height = el.getBoundingClientRect().height;
    const top = Math.min(header + GAP, window.innerHeight - height - GAP);
    el.style.setProperty('--dressi-sticky-top', `${Math.round(top)}px`);
  };

  const init = () => {
    const panels = document.querySelectorAll(SELECTOR);
    if (!panels.length) return;
    const updateAll = () => panels.forEach(update);
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(updateAll);
      panels.forEach((el) => ro.observe(el));
    }
    window.addEventListener('resize', updateAll, { passive: true });
    desktop.addEventListener('change', updateAll);
    updateAll();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --------------------------------------------------------------------------
   Hero signature: fade in with the buttons (or, on a hero without buttons,
   with the last line of the heading)

   Focal's hero reveal (image-with-text-overlay) animates the image, the
   heading lines, then .image-overlay__text-container (the buttons). The
   free-floating signature (.dressi-overlay-image) sits outside all of those,
   so it carries `reveal` (opacity 0) and this mirrors the buttons' fade onto
   it: same keyframes, same duration, and the same start time, so the two
   arrive together. If the buttons have already finished (a theme-editor
   reload skips straight to the end), the signature is simply shown.
   -------------------------------------------------------------------------- */
(() => {
  const init = () => {
    document.querySelectorAll('image-with-text-overlay[reveal-on-scroll]').forEach((hero) => {
      // The floating signature, and on heroes without buttons the phone
      // signature under the heading (.dressi-overlay-below[reveal]).
      const signatures = [...hero.querySelectorAll('.dressi-overlay-image[reveal], .dressi-overlay-below[reveal]')];
      // Follow the buttons; with none, follow the last line of the heading.
      const buttons = hero.querySelector('.image-overlay__text-container') ||
        [...hero.querySelectorAll('.image-overlay__content [reveal]')].filter((el) => !signatures.includes(el)).pop();
      if (!signatures.length) return;

      const show = () => { signatures.forEach((el) => { el.style.opacity = '1'; }); };
      if (!buttons || !('getAnimations' in buttons)) { show(); return; }

      const watch = () => {
        const anim = buttons.getAnimations()[0];
        // A just-played animation is "running" but pending, with no start
        // time yet. Keep waiting until it has one, or the signature would
        // skip its fade and pop in.
        if (anim && anim.playState === 'running' && anim.startTime !== null) {
          const timing = anim.effect.getTiming();
          signatures.forEach((signature) => {
            const mirror = signature.animate({ opacity: [0, 1] }, {
              duration: timing.duration,
              easing: timing.easing,
              fill: 'forwards'
            });
            mirror.startTime = anim.startTime;
            mirror.finished.then(show, show);
          });
          return;
        }
        // Only give up on fading once the buttons' fade is truly over.
        if ((anim && anim.playState === 'finished') || (!anim && buttons.style.opacity === '1')) {
          show();
          return;
        }
        requestAnimationFrame(watch);
      };

      // Only start watching once the hero is on screen; Focal waits for that too.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            watch();
          }
        });
        io.observe(hero);
      } else {
        show();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   DRESSi: footer newsletter sign-ups never get dropped.

   Shopify guards sign-up forms with hCaptcha. Its script only starts loading
   when someone focuses the form, and until it has attached (the form gets
   data-hcaptcha-bound) it cancels every submit silently: no message, nothing
   sent. A quick autofill-and-Subscribe hit that window (found 28 Sep 2026).

   1. Warm up: focus-in on the form as the footer approaches the screen (once
      the page has loaded), so the spam check is ready before anyone types.
   2. Safety net: if Shopify cancelled a submit because the check wasn't ready,
      wait for it (up to 8s) and submit again. It still goes through hCaptcha.
   ========================================================================== */
(() => {
  const init = () => {
    const form = document.getElementById('footer-newsletter');
    if (!form) return;

    const field = form.querySelector('input[type="email"]');
    const warmUp = () => {
      if (field) field.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
    };

    // Shopify's captcha script arrives with the page's other scripts, so only
    // start watching once the page has fully loaded; a focus-in before then
    // goes unheard.
    const watchFooter = () => {
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            warmUp();
          }
        }, { rootMargin: '600px 0px' });
        io.observe(form);
      } else {
        warmUp();
      }
    };

    if (document.readyState === 'complete') {
      setTimeout(watchFooter, 0);
    } else {
      window.addEventListener('load', () => setTimeout(watchFooter, 0), { once: true });
    }

    let lastSubmitter = null;
    form.addEventListener('click', (event) => {
      const button = event.target.closest('button[type="submit"]');
      if (button) lastSubmitter = button;
    });

    // Window listeners run after Shopify's document listener, so by now we
    // know whether it cancelled this submit.
    window.addEventListener('submit', (event) => {
      if (event.target !== form || !event.defaultPrevented) return;
      if (form.dataset.hcaptchaBound || form.dataset.dressiRetrying) return;

      form.dataset.dressiRetrying = 'true';
      warmUp();
      const started = Date.now();
      const retry = () => {
        if (form.dataset.hcaptchaBound) {
          delete form.dataset.dressiRetrying;
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(lastSubmitter && form.contains(lastSubmitter) ? lastSubmitter : undefined);
          } else if (lastSubmitter) {
            lastSubmitter.click();
          }
          return;
        }
        if (Date.now() - started > 8000) {
          delete form.dataset.dressiRetrying;
          return;
        }
        setTimeout(retry, 150);
      };
      retry();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   DRESSi: free gift in the cart (the cookbook).

   Shopify's Buy X get Y discount makes the gift free but never puts it in the
   cart, so this does: once the physical items reach the amount set in Theme
   settings > Cart > Free gift, the gift goes in (marked _dressi_gift), and it
   comes out again if the cart drops below.

   - The spend is counted as snippets/dressi-gift-bar counts it: physical lines
     at what the customer pays, minus order-level discounts.
   - If the customer removes the gift it stays out for the rest of the visit.
   - Safety net: if the discount did not make the added gift free, it comes
     straight back out. The cart never charges for something it added itself.
   - Only lines this script added are touched; a cookbook bought on purpose is
     left alone.
   ========================================================================== */
(() => {
  const configEl = document.getElementById('dressi-gift-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }
  if (!config.variantId || !(config.threshold > 0)) return;

  const routes = (window.themeVariables && window.themeVariables.routes) || {};
  const cartUrl = routes.cartUrl || '/cart';
  const DECLINED_KEY = 'dressi-gift-declined';
  const HAD_KEY = 'dressi-gift-had';

  const session = {
    get(key) {
      try { return sessionStorage.getItem(key); } catch (error) { return null; }
    },
    set(key, value) {
      try { sessionStorage.setItem(key, value); } catch (error) { /* private mode: forget */ }
    }
  };
  const declined = {
    get: () => session.get(DECLINED_KEY) === '1',
    set: () => session.set(DECLINED_KEY, '1')
  };

  const isGift = (item) => item.properties && item.properties._dressi_gift;
  const giftLines = (cart) => cart.items.filter(isGift);
  const spend = (cart) => {
    const lines = cart.items
      .filter((item) => !isGift(item) && item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const orderDiscounts = (cart.cart_level_discount_applications || [])
      .reduce((sum, discount) => sum + discount.total_allocated_amount, 0);
    return lines - orderDiscounts;
  };

  const getCart = async () => (await fetch(`${cartUrl}.js`, { cache: 'reload' })).json();
  const post = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });
  const removeGifts = (cart) => {
    const updates = {};
    giftLines(cart).forEach((item) => { updates[item.key] = 0; });
    return post(`${cartUrl}/update.js`, { updates });
  };

  let busy = false;
  let queued = false;
  // Was the gift in the cart last time we looked? Kept across pages, so a
  // removal on the cart page still counts as a no.
  let hadGift = session.get(HAD_KEY) === '1';

  const sync = async () => {
    if (busy) { queued = true; return; }
    busy = true;

    try {
      let cart = await getCart();
      const has = giftLines(cart).length > 0;
      const reached = spend(cart) >= config.threshold;
      let changed = false;

      // Gone while still over the amount, and not by us: the customer said no.
      if (hadGift && !has && reached) declined.set();

      if (reached && !has && !declined.get()) {
        await post(`${cartUrl}/add.js`, {
          items: [{ id: config.variantId, quantity: 1, properties: { _dressi_gift: 'true' } }]
        });
        cart = await getCart();
        if (giftLines(cart).some((item) => item.final_line_price > 0)) {
          await removeGifts(cart);
          declined.set(); // the discount disagrees; don't keep trying this visit
        }
        changed = true;
      } else if (has) {
        const lines = giftLines(cart);
        if (!reached) {
          await removeGifts(cart);
          changed = true;
        } else if (lines.length > 1 || lines[0].quantity > 1) {
          // Only one copy is free (e.g. the quantity was raised on the cart page).
          const updates = {};
          lines.forEach((item, index) => { updates[item.key] = index === 0 ? 1 : 0; });
          await post(`${cartUrl}/update.js`, { updates });
          changed = true;
        } else if (lines[0].final_line_price > 0) {
          await removeGifts(cart);
          changed = true;
        }
      }

      if (changed) {
        const response = await fetch(`${cartUrl}?sections=mini-cart`);
        cart = await getCart();
        cart.sections = await response.json();
        // After the theme's own re-render (it waits 600ms when something was
        // just added), so this one lands last.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart, replacementDelay: 650 }
        }));
      }

      hadGift = giftLines(cart).length > 0;
      session.set(HAD_KEY, hadGift ? '1' : '0');
    } catch (error) {
      // Leave the cart as it is; the next change tries again.
    } finally {
      busy = false;
      if (queued) {
        queued = false;
        sync();
      }
    }
  };

  document.documentElement.addEventListener('cart:updated', () => sync());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync());
  } else {
    sync();
  }
})();
